"use client";

import { createContext, useContext, useEffect, useRef, useState, useMemo, useCallback, useSyncExternalStore } from "react";
import { usePathname } from "next/navigation";
import type { AgencyBranding } from "@/app/_module/types/branding";
import { DEFAULT_BRANDING } from "@/app/_module/types/branding";
import { applyBrandFont, isBrandFont } from "@/app/_module/lib/brandFonts";
import { useUserContext } from "./userContext";
import { getApiUrl } from "@/app/_module/services/config";
import axiosInstance from "@/app/_module/services/axiosConfig";
import { isNativePlatform } from "@/lib/capacitor/platform";
import { setItem } from "@/lib/capacitor/storage";

/**
 * Normalize any CSS color (hex, rgb, hsl, named) to a valid CSS color string.
 * Uses the browser's canvas to parse and normalize the color.
 * Returns null if conversion fails so we can skip setting invalid values.
 */
function normalizeColor(color: string): string | null {
  if (!color || typeof color !== "string") return null;
  const trimmed = color.trim();
  if (!trimmed) return null;

  // If it's already a valid hex color, return as-is
  if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(trimmed)) {
    return trimmed;
  }

  // If it's already a valid CSS color function (rgb, hsl, oklch, etc.), return as-is
  if (/^(rgb|hsl|oklch|lab|lch|hwb)a?\(/.test(trimmed)) {
    return trimmed;
  }

  // Use canvas to parse any other format (named colors, shorthand, etc.)
  try {
    const ctx = document.createElement("canvas").getContext("2d");
    if (!ctx) return null;
    ctx.fillStyle = "#000000"; // reset
    ctx.fillStyle = trimmed;
    const computed = ctx.fillStyle;
    // If the canvas didn't recognize it, it stays as the reset value
    if (computed === "#000000" && trimmed.toLowerCase() !== "#000000" && trimmed.toLowerCase() !== "black") {
      return null;
    }
    return computed; // browser normalizes to #rrggbb
  } catch {
    return null;
  }
}

interface BrandingContextValue {
  branding: AgencyBranding;
  isWhiteLabeled: boolean;
  isLoading: boolean;
  refreshBranding?: () => Promise<void>;
}

const BrandingContext = createContext<BrandingContextValue>({
  branding: DEFAULT_BRANDING,
  isWhiteLabeled: false,
  isLoading: true,
  refreshBranding: undefined,
});

export const useBranding = () => useContext(BrandingContext);

// Cache key for persisting branding across page refreshes
const BRANDING_CACHE_KEY = "insura_branding_cache";

// Module-level cache for useSyncExternalStore — getSnapshot must return
// a stable reference when the underlying data hasn't changed, otherwise
// React triggers an infinite re-render loop.
let _cachedBrandingSnapshot: Partial<AgencyBranding> | null = null;
let _cachedBrandingRaw: string | null = "\0"; // sentinel so first read always parses

function getCachedBranding(): Partial<AgencyBranding> | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(BRANDING_CACHE_KEY);
    if (raw === _cachedBrandingRaw) return _cachedBrandingSnapshot;
    _cachedBrandingRaw = raw;
    _cachedBrandingSnapshot = raw ? JSON.parse(raw) : null;
    return _cachedBrandingSnapshot;
  } catch {
    return null;
  }
}

function setCachedBranding(branding: Partial<AgencyBranding>) {
  if (typeof window === "undefined") return;
  try {
    const json = JSON.stringify(branding);
    localStorage.setItem(BRANDING_CACHE_KEY, json);
    // Update module-level cache so next getSnapshot returns the same reference
    _cachedBrandingRaw = json;
    _cachedBrandingSnapshot = branding;
  } catch {
    // Quota exceeded or private browsing — ignore
  }
}

export const BrandingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isInitializing } = useUserContext();
  const pathname = usePathname();
  const [domainBranding, setDomainBranding] = useState<Partial<AgencyBranding> | null>(null);
  const [liveBranding, setLiveBranding] = useState<Partial<AgencyBranding> | null>(null);
  // Read cached branding via useSyncExternalStore to avoid SSR hydration mismatch
  // Server returns null, client reads localStorage synchronously on first render
  const noopSubscribe = useCallback(() => () => {}, []);
  const cachedBranding = useSyncExternalStore(
    noopSubscribe,
    getCachedBranding,   // client: read localStorage
    () => null           // server: null
  );
  const [isLoading, setIsLoading] = useState(true);
  const titleObserverRef = useRef<MutationObserver | null>(null);

  // Refresh branding data (called from BrandingSettings after save/upload)
  const refreshBranding = useCallback(async () => {
    try {
      const response = await axiosInstance.get("/api/branding/my-agency");
      const raw = response as any;
      // The axios onResponse interceptor already unwraps response.data,
      // so `raw` is the HTTP body. The TransformInterceptor may double-wrap:
      // { statusCode, data: { statusCode, data: {brandName, ...} } }
      // Navigate to the deepest object that has branding fields (e.g. brandName)
      let brandingData = raw;
      if (brandingData?.data && typeof brandingData.data === "object") {
        brandingData = brandingData.data;
        if (brandingData?.data && typeof brandingData.data === "object") {
          brandingData = brandingData.data;
        }
      }
      // Ensure we got actual branding data (not a wrapper with statusCode only)
      if (brandingData && (brandingData.brandName || brandingData.primaryColor || brandingData.logoUrl)) {
        setLiveBranding(brandingData);
      }
    } catch {
      // Silent fail
    }
  }, []);

  // Step 1: On mount, check if current hostname is a custom domain
  useEffect(() => {
    async function checkDomainBranding() {
      if (typeof window === "undefined") return;

      const hostname = window.location.hostname;
      // Skip for known InsuraCentral domains and local dev
      if (
        hostname.includes("insuracentral.com") ||
        hostname === "localhost" ||
        hostname === "127.0.0.1" ||
        hostname.startsWith("192.168.") ||
        hostname.startsWith("10.") ||
        hostname.includes("onrender.com") ||
        hostname.includes("vercel.app")
      ) {
        setIsLoading(false);
        return;
      }

      try {
        const res = await fetch(getApiUrl(`branding/domain/${hostname}`));
        const data = await res.json();
        // Double-wrapped like my-agency above: { statusCode, data: { statusCode, data: {brandName, ...} } }
        const found = data?.data?.data ?? data?.data;
        if (found?.brandName) {
          setDomainBranding(found);
        }
      } catch {
        // Silent fail - use default branding
      } finally {
        setIsLoading(false);
      }
    }

    checkDomainBranding();
  }, []);

  // Step 1b: Once user is authenticated, fetch their agency's branding from the API
  // This ensures branding is always up-to-date (not just from the initial login enrichment)
  // When user logs out (user becomes null), clear live branding so we fall back to defaults
  const isOnAuthPage = pathname?.startsWith("/auth");
  useEffect(() => {
    if (isInitializing) return;
    // `user.user` is a non-null shell object before anyone is signed in — check
    // for an id, or this fired /branding/my-agency (401) for every anonymous
    // visitor and crawler that touched an app URL.
    if (!user?.user?.id || isOnAuthPage) {
      // User logged out or on auth page — clear live branding so sign-in page shows defaults
      setLiveBranding(null);
      return;
    }
    refreshBranding();
  }, [isInitializing, user?.user, refreshBranding, isOnAuthPage]);

  // Step 2: Merge branding sources (cached → domain → user agency → live updates)
  // On auth pages: only use DEFAULT_BRANDING + domainBranding (for custom domains)
  // This prevents stale user branding from leaking after logout (client-side navigation)
  const branding = useMemo((): AgencyBranding => {
    // On auth pages, ignore user/live/cached branding — only domain branding matters
    if (isOnAuthPage) {
      return {
        ...DEFAULT_BRANDING,
        ...(domainBranding || {}),
      };
    }

    const enrichedUser = user?.user as any;
    const agencyBranding = enrichedUser?.agencyBranding;
    const agencyLogoUrl = enrichedUser?.agencyLogoUrl;

    // When liveBranding exists (i.e. we fetched from API), it is authoritative —
    // don't fall back to stale agencyLogoUrl from the enriched user token.
    // Only use agencyLogoUrl for branding if the agency has white-label branding configured
    // (has a custom brandName). Otherwise it's just an agency profile pic, not a brand logo.
    // Only use agency logos for app branding if the agency is actually white-labeled
    // (has a custom brandName). Otherwise the agency profile pic replaces InsuraCentral's logo.
    const hasWhiteLabel = (liveBranding?.brandName && liveBranding.brandName !== 'InsuraCentral')
      || (agencyBranding?.brandName && agencyBranding.brandName !== 'InsuraCentral')
      || (cachedBranding?.brandName && cachedBranding.brandName !== 'InsuraCentral');

    const resolvedLogoUrl = hasWhiteLabel
      ? (liveBranding?.logoUrl || agencyLogoUrl || agencyBranding?.logoUrl || domainBranding?.logoUrl || cachedBranding?.logoUrl || undefined)
      : (domainBranding?.logoUrl || undefined); // Non-white-labeled: only use domain branding logo, else default favicon

    // Strip logo/favicon from non-white-labeled sources to prevent agency profile pic leaking
    const safeLiveBranding = liveBranding ? { ...liveBranding } : {};
    const safeBranding = agencyBranding ? { ...agencyBranding } : {};
    const safeCached = cachedBranding ? { ...cachedBranding } : {};
    if (!hasWhiteLabel) {
      delete safeLiveBranding.logoUrl;
      delete safeLiveBranding.faviconUrl;
      delete safeBranding.logoUrl;
      delete safeBranding.faviconUrl;
      delete safeCached.logoUrl;
      delete safeCached.faviconUrl;
    }

    return {
      ...DEFAULT_BRANDING,
      // The cache only covers first paint. Kept under the live branding, a field the agency
      // cleared (a font, a color) lived on from it and was written back to it on every load.
      ...(liveBranding ? {} : safeCached),
      ...(domainBranding || {}),
      ...safeBranding,
      ...safeLiveBranding,
      logoUrl: resolvedLogoUrl,
    };
  }, [user, domainBranding, liveBranding, cachedBranding, isOnAuthPage]);

  // Step 2b: Persist branding to localStorage for instant load on next refresh
  useEffect(() => {
    const toCache: Partial<AgencyBranding> = {};
    if (branding.brandName && branding.brandName !== DEFAULT_BRANDING.brandName) toCache.brandName = branding.brandName;
    if (branding.primaryColor) toCache.primaryColor = branding.primaryColor;
    if (branding.secondaryColor) toCache.secondaryColor = branding.secondaryColor;
    if (branding.accentColor) toCache.accentColor = branding.accentColor;
    if (branding.sidebarColor) toCache.sidebarColor = branding.sidebarColor;
    if (branding.sidebarTextColor) toCache.sidebarTextColor = branding.sidebarTextColor;
    if (branding.sidebarAccentColor) toCache.sidebarAccentColor = branding.sidebarAccentColor;
    if (branding.sidebarAccentTextColor) toCache.sidebarAccentTextColor = branding.sidebarAccentTextColor;
    if (branding.sidebarHeaderTextColor) toCache.sidebarHeaderTextColor = branding.sidebarHeaderTextColor;
    if (isBrandFont(branding.textFont)) toCache.textFont = branding.textFont;
    if (isBrandFont(branding.headingFont)) toCache.headingFont = branding.headingFont;
    if (branding.logoUrl) toCache.logoUrl = branding.logoUrl;
    if (branding.faviconUrl) toCache.faviconUrl = branding.faviconUrl;
    if (branding.brandTagline && branding.brandTagline !== DEFAULT_BRANDING.brandTagline) toCache.brandTagline = branding.brandTagline;
    if (Object.keys(toCache).length > 0) {
      setCachedBranding(toCache);
    }
  }, [branding]);

  // Step 3: Inject CSS variables when branding changes
  useEffect(() => {
    if (!branding) return;
    const root = document.documentElement;

    // Helper: set a CSS variable with a normalized color value
    // The @theme inline system in globals.css uses CSS variables directly as color values
    const setColorVar = (varName: string, color: string | undefined) => {
      if (!color) return;
      const normalized = normalizeColor(color);
      if (normalized) root.style.setProperty(varName, normalized);
    };

    setColorVar("--primary", branding.primaryColor);
    setColorVar("--secondary", branding.secondaryColor);
    setColorVar("--accent", branding.accentColor);

    if (branding.sidebarColor) {
      const color = normalizeColor(branding.sidebarColor);
      if (color) {
        root.style.setProperty("--sidebar", color);
        root.style.setProperty("--sidebar-background", color);
        root.style.setProperty("--sidebar-primary", color);
        if (!branding.sidebarAccentColor) {
          root.style.setProperty("--sidebar-accent", color);
        }
      }
    }
    if (branding.sidebarAccentColor) {
      setColorVar("--sidebar-accent", branding.sidebarAccentColor);
    }
    if (branding.sidebarTextColor) {
      const color = normalizeColor(branding.sidebarTextColor);
      if (color) {
        root.style.setProperty("--sidebar-foreground", color);
        root.style.setProperty("--sidebar-primary-foreground", color);
        if (!branding.sidebarAccentTextColor) {
          root.style.setProperty("--sidebar-accent-foreground", color);
        }
      }
    }
    if (branding.sidebarAccentTextColor) {
      setColorVar("--sidebar-accent-foreground", branding.sidebarAccentTextColor);
    }
    setColorVar("--sidebar-header-foreground", branding.sidebarHeaderTextColor);
    applyBrandFont("text", branding.textFont);
    applyBrandFont("heading", branding.headingFont);

    // Update favicon dynamically — update ALL icon link elements
    const faviconSrc = branding.faviconUrl || branding.logoUrl;
    if (faviconSrc) {
      const iconLinks = document.querySelectorAll(
        "link[rel='icon'], link[rel='shortcut icon'], link[rel='apple-touch-icon']"
      );
      iconLinks.forEach((link) => {
        (link as HTMLLinkElement).href = faviconSrc;
      });
      // If no icon links exist yet, create one
      if (iconLinks.length === 0) {
        const newLink = document.createElement("link");
        newLink.rel = "icon";
        newLink.href = faviconSrc;
        document.head.appendChild(newLink);
      }
    }

    return () => {
      // Cleanup on unmount — remove all overridden variables
      root.style.removeProperty("--primary");
      root.style.removeProperty("--secondary");
      root.style.removeProperty("--accent");
      root.style.removeProperty("--sidebar");
      root.style.removeProperty("--sidebar-background");
      root.style.removeProperty("--sidebar-primary");
      root.style.removeProperty("--sidebar-accent");
      root.style.removeProperty("--sidebar-foreground");
      root.style.removeProperty("--sidebar-primary-foreground");
      root.style.removeProperty("--sidebar-accent-foreground");
      root.style.removeProperty("--sidebar-header-foreground");
      root.style.removeProperty("--default-font-family");
      root.style.removeProperty("--brand-heading-font");
    };
  }, [branding]);

  // Step 4: Keep document title in sync with brand name across route changes
  // Uses a MutationObserver to catch & override any Next.js title changes
  useEffect(() => {
    // Not before the domain check lands: until then brandName is the default, and a custom domain's
    // tab flashed "InsuraCentral" over its server-rendered title on every refresh.
    if (!branding.brandName || isLoading) return;

    // Apply immediately
    document.title = branding.brandName;

    // Watch for Next.js overwriting the <title> on route transitions
    titleObserverRef.current?.disconnect();
    const titleEl = document.querySelector("title");
    if (titleEl) {
      const observer = new MutationObserver(() => {
        if (document.title !== branding.brandName) {
          document.title = branding.brandName;
        }
      });
      observer.observe(titleEl, { childList: true, characterData: true, subtree: true });
      titleObserverRef.current = observer;
    }

    return () => {
      titleObserverRef.current?.disconnect();
      titleObserverRef.current = null;
    };
  }, [branding.brandName, pathname, isLoading]);

  const isWhiteLabeled = branding.brandName !== "InsuraCentral";

  // The iPhone Duo's outer screen (ios/App/App/SceneDelegate.swift, BackScreen) reads
  // this from UserDefaults: the agency's logo and name when white-labeled, otherwise
  // InsuraCentral's mark.
  useEffect(() => {
    if (!isNativePlatform()) return;
    setItem("backScreenBranding", JSON.stringify({
      whiteLabeled: isWhiteLabeled,
      brandName: branding.brandName,
      logoUrl: branding.logoUrl || branding.faviconUrl || null,
      primaryColor: branding.primaryColor ? normalizeColor(branding.primaryColor) : null,
    })).catch(() => {});
  }, [isWhiteLabeled, branding.brandName, branding.logoUrl, branding.faviconUrl, branding.primaryColor]);

  const value = useMemo(
    () => ({
      branding,
      isWhiteLabeled,
      isLoading: isLoading || isInitializing,
      refreshBranding,
    }),
    [branding, isWhiteLabeled, isLoading, isInitializing, refreshBranding]
  );

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
};
